import {
  Box,
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Select,
  VStack,
  HStack,
  InputGroup,
  InputRightElement,
  Tooltip,
  Spinner,
  Divider,
  Text,
} from "@chakra-ui/react";
import { DatePicker } from "../../components/DriverForm/DatePicker";
import UploadImage from "../../components/DriverForm/UploadImage";
import { useDriverFormController } from "./DriverForm.controller";
import { Footer } from "../../components/Landing/Footer";
import Header from "../../components/DriverForm/Header";
import ColorSelect from "../../components/DriverForm/ColorSelect";
import { colorOptions } from "../../utils/colorOptions";

const DriverForm = () => {
  const controller = useDriverFormController();

  // Define standardized input styles to use across all form inputs
  const inputStyles = {
    borderColor: "#7E7D7D",
    _hover: { borderColor: "#7E7D7D" },
    _focus: { borderColor: "#7E7D7D", boxShadow: "none" },
    fontSize: ["sm", "md", "md"],
    fontWeight: "normal",
  };

  return (
    <VStack
      minH="100vh"
      h="auto"
      bgColor="primary.900"
      w="100%"
      gap={0}
      spacing={0}
    >
      <Header />
      <Container
        maxW="container.lg"
        py={[4, 6, 8]}
        px={[3, 4, 6]}
        mt={[4, 6, 8]}
      >
        <VStack spacing={[4, 6, 8]} align="stretch">
          <Box textAlign="center" px={[16, 24, 32]} maxW="1024px" mx="auto">
            <Heading
              as="h1"
              fontSize="25px"
              fontWeight={700}
              color="white"
              mb="20px"
            >
              Sumate como conductor a ViajáYa
            </Heading>
            <Text fontSize="16px" color="white" lineHeight="24px">
              Completá este formulario para unirte como conductor. Ingresá tus
              datos personales, información del vehículo y documentos
              requeridos. Recibirás un correo en tu email cuando revisemos tu
              solicitud. ¡Empezá a manejar y generá ingresos!
            </Text>
          </Box>

          <Box
            as="form"
            onSubmit={controller.handleSubmit(controller.onSubmit)}
            noValidate
          >
            <Divider mb={6} />

            {/* Driver Information Section */}
            <Box
              bg="primary.900"
              p={[4, 5, 6]}
              borderRadius="md"
              mb={[4, 6, 8]}
              color="white"
            >
              <Heading as="h2" size="md" mb={[4, 5, 6]} color="white">
                Datos del Conductor
              </Heading>

              <Grid
                templateColumns={[
                  "repeat(1, 1fr)",
                  "repeat(1, 1fr)",
                  "repeat(2, 1fr)",
                ]}
                gap={[4, 4, 6]}
              >
                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.firstName}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Nombre <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      {...controller.register("firstName")}
                      placeholder="Ingresá su nombre"
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.firstName?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.lastName}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Apellido <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <Input
                      {...controller.register("lastName")}
                      placeholder="Ingresá su apellido"
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.lastName?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isInvalid={!!controller.errors.dni} minH="85px">
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      DNI <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <InputGroup size={["sm", "md", "md"]}>
                      <Input
                        {...controller.register("dni")}
                        placeholder="Ingresá su DNI"
                        onBlur={controller.onBlurDni}
                        {...inputStyles}
                      />
                      <InputRightElement h="full">
                        {controller.validateDriverLoading && (
                          <Tooltip label="Cargando">
                            <Spinner />
                          </Tooltip>
                        )}
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>
                      {controller.errors.dni?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.phone}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Teléfono <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <Input
                      {...controller.register("phone")}
                      placeholder="Ingresá su teléfono"
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.phone?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.state}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Provincia <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <Select
                      {...controller.register("state", {
                        required: "La provincia es obligatoria",
                      })}
                      defaultValue="Córdoba"
                      {...inputStyles}
                    >
                      <option value="Córdoba">Córdoba</option>
                    </Select>
                    <FormErrorMessage>
                      {controller.errors.state?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isInvalid={!!controller.errors.city} minH="85px">
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Localidad <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <Select
                      {...controller.register("city", {
                        required: "La localidad es obligatoria",
                      })}
                      defaultValue="Córdoba"
                      {...inputStyles}
                    >
                      <option value="Córdoba">Córdoba</option>
                    </Select>
                    <FormErrorMessage>
                      {controller.errors.city?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.email}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Email <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <Input
                      {...controller.register("email")}
                      type="email"
                      placeholder="Ingresá su email"
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.email?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.birthDate}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Fecha de Nacimiento{" "}
                      <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <DatePicker
                      selectedDate={
                        controller.getValues("birthDate")
                          ? new Date(controller.getValues("birthDate"))
                          : undefined
                      }
                      onChange={(date) => {
                        controller.setValue("birthDate", date as Date, {
                          shouldValidate: true,
                        });
                      }}
                      maxDate={controller.eighteenYearsAgo}
                      placeholder="Seleccionar fecha de nacimiento"
                    />
                    <FormErrorMessage>
                      {controller.errors.birthDate?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.gender}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Género <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <Select
                      {...inputStyles}
                      {...controller.register("gender", {
                        required: "El género es obligatorio",
                      })}
                    >
                      <option value="male">Masculino</option>
                      <option value="female">Femenino</option>
                      <option value="other">Otro</option>
                    </Select>
                    <FormErrorMessage>
                      {controller.errors.gender?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Divider mb={6} />

            {/* Vehicle Information Section */}
            <Box
              bg="primary.900"
              p={[4, 5, 6]}
              borderRadius="md"
              boxShadow="md"
              mb={[4, 6, 8]}
              color="white"
            >
              <Heading as="h2" size="md" mb={[4, 5, 6]} color="white">
                Datos del Vehículo
              </Heading>

              <Grid
                templateColumns={[
                  "repeat(1, 1fr)",
                  "repeat(1, 1fr)",
                  "repeat(2, 1fr)",
                ]}
                gap={[4, 4, 6]}
              >
                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.licensePlate}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Patente <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <InputGroup size={["sm", "md", "md"]}>
                      <Input
                        {...controller.register("licensePlate")}
                        placeholder="Ingresá la patente"
                        onBlur={controller.onBlurLicensePlate}
                        {...inputStyles}
                      />
                      <InputRightElement h="full">
                        {controller.validateVehicleLoading && (
                          <Tooltip label="Cargando">
                            <Spinner />
                          </Tooltip>
                        )}
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>
                      {controller.errors.licensePlate?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.vehicleType}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Tipo de Vehículo <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Select
                      {...controller.register("vehicleType")}
                      placeholder="Seleccione el tipo de vehículo"
                      defaultValue="particular"
                      {...inputStyles}
                    >
                      <option value="particular">Particular</option>
                      <option value="taxi">Taxi</option>
                      <option value="remis">Remis</option>
                    </Select>
                    <FormErrorMessage>
                      {controller.errors.vehicleType?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.brand}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Marca <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      {...controller.register("brand")}
                      placeholder="Ingresá la marca"
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.brand?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.model}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Modelo <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      {...controller.register("model")}
                      placeholder="Ingresá el modelo"
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.model?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  {/* <FormControl
                    isInvalid={!!controller.errors.color}
                    minH="85px"
                  >
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Color <span style={{ color: "red" }}>*</span>
                    </FormLabel>

                    <Input
                      id="color"
                      type="color"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      borderRadius="4px"
                      p={0}
                      size="lg"
                      border="0px"
                      bgColor="gray.700"
                      _focus={{
                        borderColor: "gray.800",
                        boxShadow: "none",
                      }}
                      {...controller.register("color")}
                      placeholder="Número de placa"
                    />
                    <FormErrorMessage>
                      {controller.errors.color?.message}
                    </FormErrorMessage>
                  </FormControl> */}

                  <FormControl w="100%" isInvalid={!!controller.errors?.color}>
                    <FormLabel>Color <span style={{ color: "red" }}>*</span></FormLabel>
                    <ColorSelect
                      options={colorOptions}
                      onChange={(value: string) => controller.setValue("color", value)}
                      value={controller.watch("color")}
                      placeholder="Color"
                      error={!!controller.errors?.color}
                      onError={(hasError) =>
                        hasError ? null : controller.clearErrors("color")
                      }
                    />
                    <FormErrorMessage>{`${controller.errors.color?.message}`}</FormErrorMessage>
                  </FormControl>

                </GridItem>

                <GridItem>
                  <FormControl isInvalid={!!controller.errors.year} minH="85px">
                    <FormLabel fontSize={["sm", "md", "md"]}>
                      Año <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      {...controller.register("year")}
                      type="number"
                      placeholder="Ingresá el año"
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.year?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Divider mb={6} />

            {/* Documents Upload Section */}
            <Box
              bg="primary.900"
              p={[4, 5, 6]}
              borderRadius="md"
              boxShadow="md"
              mb={8}
              color="white"
            >
              <Heading as="h2" size="md" mb={[4, 5, 6]} color="white">
                Documentación
              </Heading>

              <Grid
                templateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
                gap={[3, 4, 5]}
              >
                <GridItem>
                  <UploadImage
                    label="Foto de DNI - Frente"
                    fieldName="dniFrontPhoto"
                    isRequired={true}
                    isInvalid={!!controller.errors.dniFrontPhoto}
                    errorMessage={controller.errors.dniFrontPhoto?.message}
                    controller={controller}
                    clearErrors={controller.clearErrors}
                    alt="Foto de DNI - Frente"
                  />
                </GridItem>

                <GridItem>
                  <UploadImage
                    label="Foto de DNI - Dorso"
                    fieldName="dniBackPhoto"
                    isRequired={true}
                    isInvalid={!!controller.errors.dniBackPhoto}
                    errorMessage={controller.errors.dniBackPhoto?.message}
                    controller={controller}
                    clearErrors={controller.clearErrors}
                    alt="Foto de DNI - Dorso"
                  />
                </GridItem>

                <GridItem>
                  <UploadImage
                    label="Foto de Licencia - Frente"
                    fieldName="licenseFrontPhoto"
                    isRequired={true}
                    isInvalid={!!controller.errors.licenseFrontPhoto}
                    errorMessage={controller.errors.licenseFrontPhoto?.message}
                    controller={controller}
                    clearErrors={controller.clearErrors}
                    alt="Foto de Licencia - Frente"
                  />
                </GridItem>

                <GridItem>
                  <UploadImage
                    label="Foto de Licencia - Dorso"
                    fieldName="licenseBackPhoto"
                    isRequired={true}
                    isInvalid={!!controller.errors.licenseBackPhoto}
                    errorMessage={controller.errors.licenseBackPhoto?.message}
                    controller={controller}
                    clearErrors={controller.clearErrors}
                    alt="Foto de Licencia - Dorso"
                  />
                </GridItem>

                <GridItem>
                  <UploadImage
                    label="Foto de Cédula - Frente"
                    fieldName="vehicleRegistrationFrontPhoto"
                    isRequired={true}
                    isInvalid={
                      !!controller.errors.vehicleRegistrationFrontPhoto
                    }
                    errorMessage={
                      controller.errors.vehicleRegistrationFrontPhoto?.message
                    }
                    controller={controller}
                    clearErrors={controller.clearErrors}
                    alt="Foto de Cédula - Frente"
                  />
                </GridItem>

                <GridItem>
                  <UploadImage
                    label="Foto de Cédula - Dorso"
                    fieldName="vehicleRegistrationBackPhoto"
                    isRequired={true}
                    isInvalid={!!controller.errors.vehicleRegistrationBackPhoto}
                    errorMessage={
                      controller.errors.vehicleRegistrationBackPhoto?.message
                    }
                    controller={controller}
                    clearErrors={controller.clearErrors}
                    alt="Foto de Cédula - Dorso"
                  />
                </GridItem>

                <GridItem>
                  <UploadImage
                    label="Foto de Perfil"
                    fieldName="profilePhoto"
                    isRequired={true}
                    isInvalid={!!controller.errors.profilePhoto}
                    errorMessage={controller.errors.profilePhoto?.message}
                    controller={controller}
                    clearErrors={controller.clearErrors}
                    alt="Perfil"
                  />
                </GridItem>
              </Grid>
            </Box>

            <Divider mb={6} />

            <HStack justifyContent="center" mt={[6, 7, 10]}>
              <Button
                type="submit"
                bgColor="white"
                color="primary.800"
                size={["md", "lg", "lg"]}
                isLoading={controller.isSubmitting}
                loadingText="Enviando"
              >
                Registrar Conductor
              </Button>
            </HStack>
          </Box>
        </VStack>
      </Container>
      <Footer hasSectionBtn={false} />
    </VStack>
  );
};

export default DriverForm;
